import { z } from "zod";

export const contractTypeValues = [
  "rolling_month_to_month",
  "fixed_term_annual",
  "fixed_term_months",
  "fixed_term_then_rolling",
] as const;

export const renewalModeValues = [
  "auto_renew",
  "manual_review",
  "non_renewing",
] as const;

export const contractStatusValues = [
  "draft",
  "active",
  "pending_signature",
  "expired",
  "terminated",
] as const;

export const budgetTypeValues = [
  "retainer",
  "recurring_services",
  "project",
  "ad_hoc",
] as const;

export const budgetRecurrenceTypeValues = [
  "monthly",
  "annual",
  "custom_period",
  "one_off",
] as const;

export const revenueCategoryValues = [
  "recurring",
  "one_off",
  "project",
] as const;

export const billingModeValues = [
  "covered",
  "overflow_billable",
  "fixed_fee_track_only",
  "always_billable",
  "non_billable",
] as const;

export const serviceTypeKeyValues = [
  "hosting",
  "support",
  "development",
  "design",
  "seo",
  "strategy",
  "domains",
  "email",
  "project_delivery",
  "maintenance",
] as const;

export const renewalEventTypeValues = [
  "renewal_review",
  "price_review",
  "notice_deadline",
] as const;

export const renewalEventStatusValues = [
  "pending",
  "completed",
  "cancelled",
] as const;

export const contractTypeSchema = z.enum(contractTypeValues);
export const renewalModeSchema = z.enum(renewalModeValues);
export const contractStatusSchema = z.enum(contractStatusValues);
export const budgetTypeSchema = z.enum(budgetTypeValues);
export const budgetRecurrenceTypeSchema = z.enum(budgetRecurrenceTypeValues);
export const revenueCategorySchema = z.enum(revenueCategoryValues);
export const billingModeSchema = z.enum(billingModeValues);
export const serviceTypeKeySchema = z.enum(serviceTypeKeyValues);
export const renewalEventTypeSchema = z.enum(renewalEventTypeValues);
export const renewalEventStatusSchema = z.enum(renewalEventStatusValues);

const nullableUuidSchema = z.string().uuid().nullable().optional();
const dateInputSchema = z.coerce.date();

export const clientAccountManagerUpdateSchema = z.object({
  accountManagerUserId: nullableUuidSchema,
});

export const contractUpsertSchema = z.object({
  clientId: z.string().uuid(),
  name: z.string().min(1, "Contract name is required").max(255),
  contractOwnerUserId: nullableUuidSchema,
  contractType: contractTypeSchema,
  startDate: dateInputSchema,
  endDate: dateInputSchema.nullable().optional(),
  renewalMode: renewalModeSchema,
  noticePeriodDays: z.coerce.number().int().min(0).nullable().optional(),
  annualPriceReviewMonth: z.coerce.number().int().min(1).max(12).nullable().optional(),
  status: contractStatusSchema,
  description: z.string().max(5000).nullable().optional(),
});

export const contractOwnerUpdateSchema = z.object({
  contractOwnerUserId: nullableUuidSchema,
});

export const budgetUpsertSchema = z.object({
  contractId: nullableUuidSchema,
  serviceId: nullableUuidSchema,
  name: z.string().min(1, "Budget name is required").max(255),
  budgetType: budgetTypeSchema,
  recurrenceType: budgetRecurrenceTypeSchema,
  recurrenceIntervalMonths: z.coerce.number().int().min(1).nullable().optional(),
  startDate: dateInputSchema,
  endDate: dateInputSchema.nullable().optional(),
  revenueCategory: revenueCategorySchema,
  rolloverEnabled: z.boolean().default(false),
  rolloverCapMinutes: z.coerce.number().int().min(0).nullable().optional(),
  budgetedMinutes: z.coerce.number().int().min(0).nullable().optional(),
  budgetedAmount: z.string().nullable().optional(),
  currency: z.string().min(3).max(3).default("GBP"),
  notes: z.string().max(5000).nullable().optional(),
});

export const serviceTypeUpsertSchema = z.object({
  key: serviceTypeKeySchema,
  label: z.string().min(1).max(255),
  isActive: z.boolean().default(true),
  xeroRevenueAccountCode: z.string().max(50).nullable().optional(),
  xeroTrackingCategory: z.string().max(100).nullable().optional(),
  xeroTrackingOption: z.string().max(100).nullable().optional(),
  description: z.string().max(1000).nullable().optional(),
});

export const serviceItemUpsertSchema = z.object({
  budgetId: z.string().uuid(),
  serviceTypeId: z.string().uuid(),
  name: z.string().min(1).max(255),
  billingMode: billingModeSchema,
  isRecurringRevenue: z.boolean().default(false),
  requiresTask: z.boolean().default(false),
  defaultRate: z.string().nullable().optional(),
  unitPrice: z.string().nullable().optional(),
  quantity: z.coerce.number().int().min(1).default(1),
  notes: z.string().max(5000).nullable().optional(),
  isActive: z.boolean().default(true),
});

export type ContractType = z.infer<typeof contractTypeSchema>;
export type RenewalMode = z.infer<typeof renewalModeSchema>;
export type ContractStatus = z.infer<typeof contractStatusSchema>;
export type BudgetType = z.infer<typeof budgetTypeSchema>;
export type BudgetRecurrenceType = z.infer<typeof budgetRecurrenceTypeSchema>;
export type RevenueCategory = z.infer<typeof revenueCategorySchema>;
export type BillingMode = z.infer<typeof billingModeSchema>;
export type ServiceTypeKey = z.infer<typeof serviceTypeKeySchema>;
export type RenewalEventType = z.infer<typeof renewalEventTypeSchema>;
export type RenewalEventStatus = z.infer<typeof renewalEventStatusSchema>;
export type ClientAccountManagerUpdateInput = z.infer<
  typeof clientAccountManagerUpdateSchema
>;
export type ContractUpsertInput = z.infer<typeof contractUpsertSchema>;
export type ContractOwnerUpdateInput = z.infer<typeof contractOwnerUpdateSchema>;
export type BudgetUpsertInput = z.infer<typeof budgetUpsertSchema>;
export type ServiceTypeUpsertInput = z.infer<typeof serviceTypeUpsertSchema>;
export type ServiceItemUpsertInput = z.infer<typeof serviceItemUpsertSchema>;
